/**
 * Stations (the station workspaces plan, docs/plans/2026-10-06_station-workspaces.md, step 1; migration 0204; the Go
 * mirror is platform-core/internal/twin/stations.go): a station is a named group of plant-model equipment, the floor's
 * word for a group of machines (Melt is the furnace, the Mg station, the ladle and the spectrometer), with the people
 * assigned to it. Assignment decides what a person sees first, never what they may see.
 *
 *   GET    /v1/twin/stations                         the org's stations, in their order (any member)
 *   POST   /v1/twin/stations                         {name, line?}: a new one, last, empty (org admin)
 *   GET    /v1/twin/stations/coverage                every entity of the plant model as the creator lists it, data first
 *   PATCH  /v1/twin/stations/{station_id}            {name?, line?, ordinal?} (org admin)
 *   DELETE /v1/twin/stations/{station_id}            the station goes; its equipment and people stay (org admin)
 *   PUT    /v1/twin/stations/{station_id}/entities   {entities}: the whole set (org admin)
 *   PUT    /v1/twin/stations/{station_id}/members    {members}: the whole set (org admin)
 *   GET    /v1/twin/me/station                       the station the caller lands on, and the ones they belong to
 *   PUT    /v1/twin/me/station                       {station_id}: a uuid, or null to clear the choice
 *
 * Not the one-machine capture view's `/v1/twin/stations/{entity_id}` read (./twin-station), which is a machine, not a
 * group: the two share a path prefix and nothing else. The plant model's STATION entity kind is a third thing again,
 * shown as its word through lib/plant/kind-labels.
 *
 * The coverage rows also carry `stations`, the stations that hold each entity, so a write to a station's equipment (and
 * a delete) reads the coverage again, and a write to its members (and a delete) reads the caller's landing station
 * again. Every read is gated as the sibling twin modules gate theirs: the live build flag, a signed-in session and a
 * resolved org.
 */
import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { useAuth } from "@/lib/auth";
import { useOrg } from "@/lib/org-context";
import type { Paginated } from "./types";
import { STATION_BOARD_PREFIXES } from "./station-board";

/** A station's process line: one of the plant model's lanes, or "" for none. */
export type StationLine = "" | "melt" | "sand" | "core" | "mould" | "finishing" | "lab" | "tooling";

/** What a person does at a station. `operator` is the server's default. */
export type StationRole = "operator" | "lead" | "engineer";

/** One person of a station. `email` and `display_name` are read only, filled by the server. */
export interface StationMember {
  user_id: string;
  role: StationRole;
  email?: string;
  display_name?: string;
}

/** A station: a named group of plant-model equipment and the people assigned to it. */
export interface Station {
  id: string;
  name: string;
  line: StationLine;
  ordinal: number;
  /** Plant-model entity ids, sorted. */
  entities: string[];
  /** Current members of the org only. */
  members: StationMember[];
  created_at: string;
}

/** A new station's body: `name` 1 to 80 characters, unique in the org whatever its case (409 station_name_taken). */
export interface StationCreate {
  name: string;
  line?: StationLine;
}

/** A change to a station: any of the three; the rest stay. */
export interface StationPatch {
  name?: string;
  line?: StationLine;
  ordinal?: number;
}

/** One person in a members write; `role` defaults to operator on the server. */
export interface StationMemberPut {
  user_id: string;
  role?: StationRole;
}

/** Where an entity's data comes from: an imported file's data source, a device, data entry, or a computed tag. */
export type StationSourceKind = "sheet" | "gateway" | "manual" | "computed";

export interface StationCoverageSource {
  kind: StationSourceKind;
  name: string;
}

/**
 * One entity of the plant model as the station creator lists it. `line` is the entity's own stored line, "" when it
 * carries none (the web derives one with lineForEntity). `has_data`: a tag of it has a profiled day, or a gateway device
 * behind its tags reported within 30 days. `latest_day`: the plant day of the later of the two, null when neither.
 */
export interface StationCoverageRow {
  entity_id: string;
  name: string;
  /** The entity's kind as stored (EQUIPMENT, STATION, INSTRUMENT, TRANSPORT): shown through entityClassLabel. */
  kind: string;
  line: string;
  /** Distinct tags bound to it. */
  tags: number;
  sources: StationCoverageSource[];
  latest_day: string | null;
  has_data: boolean;
  /** The stations that hold it. */
  stations: string[];
}

/** The caller's landing station: the chosen one (`chosen` true), else their first, else none; and every one they are in. */
export interface MyStation {
  station_id: string | null;
  chosen: boolean;
  member_of: string[];
}

const enc = encodeURIComponent;

export const twinStations = {
  list: () => api.get<Paginated<Station>>("/v1/twin/stations"),
  coverage: () => api.get<Paginated<StationCoverageRow>>("/v1/twin/stations/coverage"),
  create: (body: StationCreate) => api.post<Station>("/v1/twin/stations", body),
  patch: (id: string, body: StationPatch) => api.patch<Station>(`/v1/twin/stations/${enc(id)}`, body),
  remove: (id: string) => api.delete<void>(`/v1/twin/stations/${enc(id)}`),
  putEntities: (id: string, entities: readonly string[]) =>
    api.put<Station>(`/v1/twin/stations/${enc(id)}/entities`, { entities }),
  putMembers: (id: string, members: readonly StationMemberPut[]) =>
    api.put<Station>(`/v1/twin/stations/${enc(id)}/members`, { members }),
  mine: () => api.get<MyStation>("/v1/twin/me/station"),
  putMine: (stationId: string | null) => api.put<MyStation>("/v1/twin/me/station", { station_id: stationId }),
};

/** The org's stations. The coverage and the caller's station sit under it, so `exact` keeps a list refresh to the list. */
export const STATIONS_KEY = ["twin-stations"] as const;
export const STATION_COVERAGE_KEY = ["twin-stations", "coverage"] as const;
export const MY_STATION_KEY = ["twin-stations", "me"] as const;

const isLiveApiFlag = import.meta.env.VITE_USE_LIVE_API === "true";

/** Live reads need the build flag AND a signed-in, org-resolved session (every /v1/twin route is org-scoped). */
function useStationsLive(): boolean {
  const { isAuthenticated } = useAuth();
  const { activeOrgId } = useOrg();
  return isLiveApiFlag && isAuthenticated && activeOrgId !== null;
}

/** The org's stations, in their order. */
export function useStationsQuery() {
  const live = useStationsLive();
  return useQuery({ queryKey: STATIONS_KEY, queryFn: twinStations.list, enabled: live, staleTime: 30_000 });
}

/**
 * The plant model's equipment as the creator lists it. Read again on each mount (staleTime 0), as the binding list is:
 * an import or a go-live binds tags without touching this key, and a machine with fresh data must not stay at "no data".
 */
export function useStationCoverageQuery() {
  const live = useStationsLive();
  return useQuery({ queryKey: STATION_COVERAGE_KEY, queryFn: twinStations.coverage, enabled: live, staleTime: 0 });
}

/** The station the caller lands on in the org (step 5's Today and station view read it; the creator does not). */
export function useMyStationQuery() {
  const live = useStationsLive();
  return useQuery({ queryKey: MY_STATION_KEY, queryFn: twinStations.mine, enabled: live, staleTime: 30_000 });
}

/** A station's equipment and people decide its watch list, its levels, the plant-wide list and who may pin. */
function readBoardsAgain(qc: QueryClient): void {
  for (const queryKey of STATION_BOARD_PREFIXES) void qc.invalidateQueries({ queryKey });
}

/** Make a station (org admin). It holds no equipment yet, so the coverage stays as it is. */
export function useCreateStation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: StationCreate) => twinStations.create(body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: STATIONS_KEY, exact: true }),
  });
}

/** Rename or re-line a station (org admin). */
export function usePatchStation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: StationPatch }) => twinStations.patch(id, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: STATIONS_KEY, exact: true }),
  });
}

/** Delete a station (org admin): the list, the coverage rows that named it and the caller's landing station change. */
export function useDeleteStation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => twinStations.remove(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: STATIONS_KEY }),
  });
}

/** Set a station's equipment, the whole set (org admin). The coverage rows carry `stations`, so they are read again. */
export function usePutStationEntities() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, entities }: { id: string; entities: readonly string[] }) => twinStations.putEntities(id, entities),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: STATIONS_KEY, exact: true });
      void qc.invalidateQueries({ queryKey: STATION_COVERAGE_KEY });
      readBoardsAgain(qc);
    },
  });
}

/** Set a station's members, the whole set (org admin). A person's landing station may follow, so it is read again. */
export function usePutStationMembers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, members }: { id: string; members: readonly StationMemberPut[] }) =>
      twinStations.putMembers(id, members),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: STATIONS_KEY, exact: true });
      void qc.invalidateQueries({ queryKey: MY_STATION_KEY });
      readBoardsAgain(qc);
    },
  });
}

/** Choose the station the caller lands on (any member), or null to clear the choice. */
export function usePutMyStation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (stationId: string | null) => twinStations.putMine(stationId),
    onSuccess: (mine) => qc.setQueryData(MY_STATION_KEY, mine),
  });
}
